<template>
  <div class="animal-result-page">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <span class="spinner-emoji">🔮</span>
        <p>加载结果中...</p>
      </div>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <div class="error-card">
        <span class="error-emoji">😢</span>
        <h2>加载失败</h2>
        <p>{{ error }}</p>
        <button @click="$router.push('/')" class="btn-primary">返回首页</button>
      </div>
    </div>

    <!-- 结果展示 -->
    <div v-else class="result-container">
      <!-- 顶部导航 -->
      <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div class="container mx-auto px-4">
          <div class="flex items-center justify-between py-4">
            <div class="flex items-center gap-3">
              <router-link to="/" class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                  <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                  <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                </svg>
                首页
              </router-link>
              <div class="flex items-center gap-2">
                <span class="text-2xl">🦁</span>
                <span class="font-semibold text-psychology-primary">动物性格测试结果</span>
              </div>
            </div>
            <button @click="goBack" class="hidden sm:inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              返回
            </button>
          </div>
        </div>
      </nav>

      <!-- 主内容区 -->
      <main class="main-content">
        <div class="container">
          <!-- 主要动物结果卡片 -->
          <div class="section">
            <AnimalResultCard 
              :animal="animalData"
              :similarity="result.similarity"
            />
          </div>

          <!-- 维度分数雷达图 -->
          <div class="section">
            <DimensionScores 
              :user-scores="result.enhancedScores || result.normalizedScores"
              :max-score="1"
              :primary-color="result.color"
            />
          </div>

          <!-- 所有动物排名 -->
          <div class="section">
            <AllAnimalsRanking 
              :ranking="enrichedRanking"
              @view-animal="viewAnimalDetail"
            />
          </div>

          <!-- 操作按钮 -->
          <div class="action-buttons">
            <!-- <button @click="shareResult" class="btn-secondary">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                <polyline points="16 6 12 2 8 6"></polyline>
                <line x1="12" x2="12" y1="2" y2="15"></line>
              </svg>
              分享结果
            </button> -->
            <button @click="retakeTest" class="btn-primary">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
              </svg>
              重新测试
            </button>
          </div>

          <!-- 个人信息（如果有） -->
          <div v-if="demographics && (demographics.nickname || demographics.gender)" class="info-section">
            <div class="info-card">
              <h4>测试信息</h4>
              <div class="info-items">
                <div v-if="demographics.nickname" class="info-item">
                  <span class="info-label">昵称:</span>
                  <span class="info-value">{{ demographics.nickname }}</span>
                </div>
                <div v-if="demographics.gender" class="info-item">
                  <span class="info-label">性别:</span>
                  <span class="info-value">{{ demographics.gender }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">完成时间:</span>
                  <span class="info-value">{{ formatDate(result.endTime) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
// import { useToast } from 'vue-toastification'
import { getSessionById } from '@/utils/sessionStorage'
import { animalArchetypes } from '@/data/animalArchetypes'
import { enhanceDimensionScores } from '@/utils/animalCalculator'
import AnimalResultCard from '@/components/result/AnimalResultCard.vue'
import DimensionScores from '@/components/result/DimensionScores.vue'
import AllAnimalsRanking from '@/components/result/AllAnimalsRanking.vue'

const router = useRouter()
const route = useRoute()
//const toast = useToast()

const loading = ref(true)
const error = ref(null)
const result = ref(null)
const demographics = ref(null)

// 构建完整的动物数据对象
const animalData = computed(() => {
  if (!result.value) return null
  
  return {
    name: result.value.name,
    emoji: result.value.emoji,
    description: result.value.description,
    traits: result.value.traits,
    careers: result.value.careers,
    compatibility: result.value.compatibility,
    color: result.value.color,
    tags: result.value.tags
  }
})

// 为排名数据补充完整的动物信息
const enrichedRanking = computed(() => {
  if (!result.value || !result.value.allAnimals) return []
  
  return result.value.allAnimals.map(item => ({
    name: item.name,
    similarity: item.similarity,
    emoji: item.emoji,
    animal: animalArchetypes[item.name]
  }))
})

onMounted(() => {
  loadResult()
})

const loadResult = () => {
  try {
    const sessionId = route.query.sessionId
    
    if (!sessionId) {
      error.value = '缺少会话ID'
      loading.value = false
      return
    }

    const session = getSessionById(sessionId)
    
    if (!session) {
      error.value = '找不到测试会话，请重新测试'
      loading.value = false
      return
    }

    if (!session.completed) {
      error.value = '测试尚未完成'
      loading.value = false
      return
    }

    result.value = session.results
    demographics.value = session.demographics

    if (!result.value) {
      error.value = '结果数据不存在'
      loading.value = false
      return
    }

    // 基于原始归一化分数计算增强分数（仅用于展示，不修改原始结果）
    if (result.value.normalizedScores) {
      console.log('正在计算展示用的增强分数...')
      
      const enhancedScores = enhanceDimensionScores(result.value.normalizedScores, {
        power: 0.7,
        minEnhancement: 1.1,
        maxEnhancement: 1.5
      })
      
      // 打印增强前后的数值对比
      console.log('========== 维度分数增强算法（仅用于展示）==========')
      console.log('增强前（原始归一化分数）:')
      const dimNames = {
        DOM: '支配性',
        STR: '策略性',
        COM: '社群性',
        SOL: '独处性',
        AGI: '敏捷性',
        SEC: '安全性',
        AES: '美学性'
      }
      
      Object.keys(result.value.normalizedScores).forEach(dim => {
        const before = result.value.normalizedScores[dim]
        const after = enhancedScores[dim]
        const change = ((after - before) / before * 100).toFixed(1)
        const changeSymbol = change > 0 ? '↑' : '↓'
        console.log(`  ${dimNames[dim] || dim}: ${(before * 100).toFixed(1)}% → ${(after * 100).toFixed(1)}% (${changeSymbol}${Math.abs(change)}%)`)
      })
      
      const beforeMax = Math.max(...Object.values(result.value.normalizedScores))
      const afterMax = Math.max(...Object.values(enhancedScores))
      const beforeMin = Math.min(...Object.values(result.value.normalizedScores))
      const afterMin = Math.min(...Object.values(enhancedScores))
      console.log(`\n对比统计:`)
      console.log(`  最高分: ${(beforeMax * 100).toFixed(1)}% → ${(afterMax * 100).toFixed(1)}%`)
      console.log(`  最低分: ${(beforeMin * 100).toFixed(1)}% → ${(afterMin * 100).toFixed(1)}%`)
      console.log(`  分数范围: ${((beforeMax - beforeMin) * 100).toFixed(1)}% → ${((afterMax - afterMin) * 100).toFixed(1)}%`)
      console.log('========================================')
      
      // 将增强分数添加到结果中（仅用于展示，不保存到原始数据）
      result.value.enhancedScores = enhancedScores
    }

    console.log('加载结果成功:', result.value)
    loading.value = false
  } catch (err) {
    console.error('加载结果失败:', err)
    error.value = '加载结果时发生错误'
    loading.value = false
  }
}

const viewAnimalDetail = (animalName) => {
  // 跳转到动物详情页
  router.push({ name: 'AnimalDetail', params: { name: animalName } })
}

// const shareResult = () => {
//   // 分享功能（待实现）
//   const shareText = `我在动物性格测试中是 ${result.value.name}！匹配度 ${(result.value.similarity * 100).toFixed(1)}%`
  
//   if (navigator.share) {
//     navigator.share({
//       title: '动物性格测试结果',
//       text: shareText,
//       url: window.location.href
//     }).catch(err => {
//       console.log('分享失败:', err)
//       copyToClipboard(shareText)
//     })
//   } else {
//     copyToClipboard(shareText)
//   }
// }

// const copyToClipboard = (text) => {
//   if (navigator.clipboard) {
//     navigator.clipboard.writeText(text + '\n' + window.location.href)
//       .then(() => toast.success('结果已复制到剪贴板！'))
//       .catch(() => toast.error('复制失败'))
//   } else {
//     toast.info('请手动复制分享链接')
//   }
// }

const goBack = () => {
  router.go(-1)
}

const retakeTest = () => {
  router.push('/')
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}
</script>

<style scoped>
.animal-result-page {
  min-height: 100vh;
  background: linear-gradient(to bottom right, var(--psychology-calm) 0%, #ffffff 50%, var(--psychology-warm) 100%);
}

.loading-container,
.error-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.loading-spinner {
  text-align: center;
}

.spinner-emoji {
  font-size: 4rem;
  display: block;
  margin-bottom: 1rem;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.error-card {
  background: white;
  padding: 3rem;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.error-emoji {
  font-size: 4rem;
  display: block;
  margin-bottom: 1rem;
}

.error-card h2 {
  font-size: 1.5rem;
  color: #1a202c;
  margin-bottom: 0.5rem;
}

.error-card p {
  color: #718096;
  margin-bottom: 1.5rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.main-content {
  padding: 2rem 0 4rem 0;
}

.section {
  margin-bottom: 2rem;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 3rem 0;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: var(--psychology-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--psychology-success);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--psychology-primary-30);
}

.btn-secondary {
  background: white;
  color: #4a5568;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
  border-color: #cbd5e0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.info-section {
  margin-top: 3rem;
}

.info-card {
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.info-card h4 {
  font-size: 1.25rem;
  color: #1a202c;
  margin: 0 0 1rem 0;
}

.info-items {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  gap: 0.5rem;
}

.info-label {
  color: #718096;
  font-weight: 500;
}

.info-value {
  color: #1a202c;
  font-weight: 600;
}

@media (max-width: 768px) {
  .action-buttons {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }
}
</style>

